import axios from 'axios'
import { STORAGE_KEYS } from '../constants/config'
import type { AcadConfig, SearchParams, SearchResult, PersonalList, PersonalItem, TaxonomyNode, BrandingConfig, ModerationItem, AuditEntry } from '../types'

declare global {
  interface Window {
    ACAD_DISCOVERY_CONFIG: AcadConfig
  }
}

const config = window.ACAD_DISCOVERY_CONFIG

const api = axios.create({
  baseURL: config.API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((reqConfig) => {
  const token = localStorage.getItem(STORAGE_KEYS.ID_TOKEN)
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`
  }
  return reqConfig
})

// Search
export const searchResources = async (params: SearchParams): Promise<{ results: SearchResult[]; total: number }> => {
  const { data } = await api.post('/academic/search', params)
  return data
}

// Browse
export const browseTaxonomy = async (taxonomyId: string, parentId?: string): Promise<TaxonomyNode[]> => {
  const { data } = await api.get('/academic/browse', { params: { taxonomy_id: taxonomyId, parent_id: parentId } })
  return data.nodes ?? data
}

// Taxonomies
export const getTaxonomies = async (): Promise<{ id: string; name: string; node_count: number }[]> => {
  const { data } = await api.get('/academic/taxonomies')
  return data
}

export const getTaxonomyNodes = async (taxonomyId: string): Promise<TaxonomyNode[]> => {
  const { data } = await api.get(`/academic/taxonomies/${taxonomyId}/nodes`)
  return data
}

// Personal Lists
export const getPersonalLists = async (): Promise<PersonalList[]> => {
  const { data } = await api.get('/academic/lists')
  return data
}

export const createPersonalList = async (name: string, description?: string): Promise<PersonalList> => {
  const { data } = await api.post('/academic/lists', { name, description })
  return data
}

export const deletePersonalList = async (listId: string): Promise<void> => {
  await api.delete(`/academic/lists/${listId}`)
}

export const getListItems = async (listId: string): Promise<PersonalItem[]> => {
  const { data } = await api.get(`/academic/lists/${listId}/items`)
  return data
}

export const addListItem = async (listId: string, resourceId: string, resourceType: string, title: string): Promise<void> => {
  await api.post(`/academic/lists/${listId}/items`, { resource_id: resourceId, resource_type: resourceType, title })
}

export const removeListItem = async (listId: string, resourceId: string): Promise<void> => {
  await api.delete(`/academic/lists/${listId}/items`, { data: { resource_id: resourceId } })
}

// Sharing
export const shareResource = async (resourceId: string, email: string): Promise<void> => {
  await api.post('/academic/share', { resource_id: resourceId, email })
}

export const getSharedWithMe = async (): Promise<SearchResult[]> => {
  const { data } = await api.get('/academic/share')
  return data
}

// Connections Graph
export const exploreConnections = async (resourceId: string): Promise<{ nodes: any[]; edges: any[] }> => {
  const { data } = await api.post('/academic/connections/explore', { resource_id: resourceId })
  return data
}

export const discoverMore = async (nodeId: string, resourceTypes?: string[]): Promise<{ nodes: any[]; edges: any[] }> => {
  const { data } = await api.post('/academic/connections/discover', { node_id: nodeId, resource_types: resourceTypes })
  return data
}

// Branding
export const getBranding = async (): Promise<BrandingConfig | null> => {
  try {
    const { data } = await api.get('/academic/branding')
    return data
  } catch {
    return null
  }
}

export const updateBranding = async (branding: Partial<BrandingConfig>): Promise<BrandingConfig> => {
  const { data } = await api.put('/academic/branding', branding)
  return data
}

// Moderation
export const getModerationQueue = async (): Promise<ModerationItem[]> => {
  const { data } = await api.get('/academic/moderation')
  return data
}

export const reviewModerationItem = async (itemId: string, decision: 'approved' | 'rejected'): Promise<void> => {
  await api.put('/academic/moderation', { item_id: itemId, decision })
}

// Audit
export const getAuditLog = async (params?: { user_id?: string; action_type?: string; from?: string; to?: string }): Promise<AuditEntry[]> => {
  const { data } = await api.get('/academic/audit', { params })
  return data
}

// Resource detail
export const getResource = async (id: string): Promise<SearchResult> => {
  const { data } = await api.get(`/academic/resources/${id}`)
  return data
}

export const getJournal = async (id: string): Promise<SearchResult> => {
  const { data } = await api.get(`/academic/journals/${id}`)
  return data
}

export default api
